import type { VlmFinding } from "../../types/vlm";
import { FUEL_STATE_LABELS } from "../../lib/constants";
import { fmtDate, fmtKm } from "../../lib/formatters";
import { useUIStore } from "../../store/uiStore";
import { drawerPalette, type DrawerPalette } from "../../lib/drawerTheme";

interface Props {
  vlm: VlmFinding;
}

export function VlmSection({ vlm }: Props) {
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  return (
    <section>
      <SectionTitle palette={palette}>Imagery Analysis</SectionTitle>

      {vlm.insufficient_imagery && (
        <Notice palette={palette} kind="warn">Insufficient imagery — analysis based on metadata only.</Notice>
      )}
      {vlm.disagrees_with_backbone && (
        <Notice palette={palette} kind="alert">VLM assessment disagrees with ML backbone.</Notice>
      )}

      <Grid>
        <KV palette={palette} label="Fuel State" value={FUEL_STATE_LABELS[vlm.fuel_state]} />
        <KV palette={palette} label="Active Fire" value={vlm.active_fire_visible ? "Yes 🔥" : "No"} />
        <KV palette={palette} label="Cloud Cover" value={`${vlm.rgb_cloud_cover_pct.toFixed(0)}%`} />
        <KV palette={palette} label="Capture Date" value={fmtDate(vlm.rgb_capture_date)} />
        <KV palette={palette} label="Nearest FIRMS" value={fmtKm(vlm.firms_nearest_km)} />
        <KV palette={palette} label="FIRMS (24 h)" value={`${vlm.firms_count_24h} detections`} />
        <KV palette={palette} label="Canopy Density" value={capitalize(vlm.canopy_density)} />
        <KV palette={palette} label="WUI Type" value={wuiLabel(vlm.wui_indicator)} />
      </Grid>

      {vlm.one_sentence_caveat && (
        <div style={{ fontSize: 12, color: palette.italicCaveat, fontStyle: "italic", marginTop: 8 }}>
          {vlm.one_sentence_caveat}
        </div>
      )}
    </section>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
}

function wuiLabel(w: string) {
  const m: Record<string, string> = {
    interface: "WUI Interface",
    intermix: "WUI Intermix",
    non_wui: "Non-WUI",
    unknown: "Unknown",
  };
  return m[w] ?? w;
}

function SectionTitle({ children, palette }: { children: React.ReactNode; palette: DrawerPalette }) {
  return (
    <div style={{ fontWeight: 700, fontSize: 13, color: palette.sectionTitle, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
      {children}
    </div>
  );
}

function KV({ label, value, palette }: { label: string; value: string; palette: DrawerPalette }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: palette.kvLabel, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 13, color: palette.kvValue, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function Notice({ children, kind, palette }: { children: React.ReactNode; kind: "warn" | "alert"; palette: DrawerPalette }) {
  const bg = kind === "warn" ? palette.noticeWarnBg : palette.noticeAlertBg;
  const color = kind === "warn" ? palette.noticeWarnText : palette.noticeAlertText;
  const border = kind === "warn" ? palette.noticeWarnBorder : palette.noticeAlertBorder;
  return (
    <div style={{ background: bg, color, border: `1px solid ${border}`, fontSize: 12, padding: "6px 10px", borderRadius: 4, marginBottom: 8 }}>
      {children}
    </div>
  );
}
